import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { FileClock, Trash2, AlertTriangle, CloudOff, Layers, FileText, RotateCw, ArrowRight } from 'lucide-react';
import api, { isOfflineShapedError } from '../lib/api';
import { retryErrorToast } from '../lib/retryErrorToast';
import { trackEvent } from '../lib/analytics';
import { useReadyTranslation } from '../hooks/useReadyTranslation';
import { usePendingSummary, notifyPendingChanged } from '../hooks/usePendingSummary';
import AutoSplitModal from '../components/AutoSplitModal';
import AutoSplitSuggestionCards from '../components/AutoSplitSuggestionCards';
import { accionDeOferta } from '../lib/series7';
import CreditPackModal from '../components/CreditPackModal';
import PartsMap, { buildCardSegments } from '../components/PartsMap';
import HaveChips from '../components/HaveChips';
// NOTIFICACIONES-1 (#464): la quinta puerta del permiso del push (las otras cuatro, Panel).
import { pedirPermisoSiToca } from '../lib/push';
// pantallas-alumno (G): las piezas del alumno y las del Panel (se importan, no se copian).
import {
  Boton, APRETADO, EncabezadoDePagina, RecuadroDeEstado, BloqueEsqueleto, Ventana, BOTON_VENTANA_SECUNDARIO,
  BOTON_VENTANA_BORRAR, GiroEncima, avisarSinRed, OPACIDAD_SIN_RED,
} from '../components/alumno';
import AvisoDelPanel, { BOTON_AVISO_PRINCIPAL } from '../components/panel/AvisoDelPanel';
import ChipDeEstado from '../components/panel/ChipDeEstado';
import { CAJA_ICONO, TRAZO_ICONO, LIGA_RENGLON, AreaDe44 } from '../components/panel/anatomia';

/* pantallas-alumno (G): PENDIENTES CON EL LENGUAJE DEL PANEL (parte 2, seccion 3, y sus firmas del 19-oct).
   La logica (que se pide, cuando, que se borra, que se cobra, los avisos) es la de hoy; cambia la pintura:
     . hasta 720, centrada, con el encabezado de la casa; el saldo con los chips neutros del Panel;
     . los avisos (sin red, no cargo, cambio de plan) con el aviso del Panel; vacio y no cargo sin lista con el
       recuadro I2; cargando con el esqueleto que copia la tarjeta nueva;
     . la tarjeta de Pendientes: icono de 40, nombre de 2 renglones, bote de 40 (44 en el celular), el estado y el
       chip de caducidad, el mapa de partes de la casa, "Next: ...", la liga a la biblioteca, el recuadro rojo suave
       de la parte fallida con su boton SECUNDARIO ("Retry" o "Delete this part"), el boton principal a lo ancho de
       48 (UNO por tarjeta) y los frenos con sus ofertas como renglones del Panel;
     . las ventanas de borrar con el estilo de ventana del alumno (440, su X de hoy, el rojo de borrar);
     . P6: sin red con la lista ya cargada, los botones que mandan algo (procesar, reintentar, borrar, comprar) se ven
       apagados y al tocarlos dicen "You need a connection";
     . P7 (medido): despues de "Pending parts deleted" la tarjeta se quita sola con el resumen nuevo del servidor;
     . PE23: la marca de llegada por liga directa es un borde naranja con halo, igual en la web y en la app. */

function daysUntil(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / 86400000));
}

// P-F3 (firma de Carlos) — LA LINEA DE CADUCIDAD SOLO APARECE CUANDO YA IMPORTA.
// Si al pendiente le faltan semanas, avisarlo cada vez que se abre la pagina es ruido:
// no hay nada que hacer con ese dato todavia. A 14 dias o menos si lo hay. El rojo sigue
// donde estaba (7 dias), asi que la linea nace apagada y se enciende una sola vez.
// pantallas-alumno (G): la linea es ahora el chip de caducidad (rojo / gris del Panel), con los MISMOS cortes.
const EXPIRY_NOTICE_DAYS = 14;
const EXPIRY_URGENT_DAYS = 7;

// ── TANDA L (firma de Carlos, 2026-08-08) — LOS DOS NÚMEROS DE LA PARTE ROTA, Y LA
//    TOLERANCIA A QUE NO VENGAN ──
//
// `retry_cost` y `retry_attempts_left` los manda el resumen del servidor (documents.py,
// `_retry_facts`). Durante una ventana de despliegue puede llegar un resumen VIEJO que no
// los trae. Los dos datos sólo pueden ENCENDER un apagado; su ausencia jamás inventa uno.
const tieneNumero = (v) => typeof v === 'number' && Number.isFinite(v);

// El estado del botón de una parte rota, calculado en un solo lugar para que la etiqueta,
// el apagado, el renglón explicativo y la salida no puedan contradecirse.
//   'desconocido' → el resumen no trae los datos (ventana de despliegue): como hoy.
//   'agotado'     → 0 intentos restantes: reintentar ya no es una salida; borrar sí.
//   'sin_saldo'   → vivo, pero el saldo no paga ESA parte.
//   'vivo'        → se puede reintentar ahora.
export function estadoDeParteRota(parte, creditosDisponibles) {
  const intentos = parte?.retry_attempts_left;
  const costo = parte?.retry_cost;
  if (tieneNumero(intentos) && intentos <= 0) return 'agotado';
  if (!tieneNumero(costo)) return 'desconocido';
  if (tieneNumero(creditosDisponibles) && creditosDisponibles < costo) return 'sin_saldo';
  return 'vivo';
}

const TARJETA_PENDIENTE = 'relative flex flex-col gap-4 rounded-[18px] border bg-[#141414] p-4 md:p-[18px]';
const MARCA_DE_LLEGADA = 'border-[#F27A2E] shadow-[0_0_0_3px_rgba(242,122,46,0.25)]';
const RENGLON_FRENO = 'text-[14px] leading-5 text-[#C8C8C8]';

/* La liga del Panel (13 px seminegrita naranja con flecha) con area de 44. Solo se apaga sin red: al 38
   (seccion 4.1 del encargo). Medicion (PE3, PE15): debajo de "Free up a space" va la oferta a 8 px, que le comia el
   area centrada (39); el area sube: 20 arriba y 6 abajo de la liga de un renglon, sin entrar al control de abajo. */
const AREA_ARRIBA_DE_LA_LIGA = 20;
function Liga({ onClick, testid, children, apagada, alTocarApagada }) {
  return (
    <button type="button" data-testid={testid}
      onClick={apagada ? alTocarApagada : onClick}
      aria-disabled={apagada || undefined}
      style={apagada ? { opacity: OPACIDAD_SIN_RED } : undefined}
      className={`${LIGA_RENGLON} self-start text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-white rounded`}>
      <AreaDe44 arriba={AREA_ARRIBA_DE_LA_LIGA} />
      <span className="min-w-0">{children}</span>
      <ArrowRight className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
    </button>
  );
}

/* El bote de la tarjeta: 40 (44 en el celular). Solo se apaga sin red: al 38 (seccion 4.1). */
function Bote({ onClick, testid, etiqueta, apagado, alTocarApagado }) {
  return (
    <button type="button" data-testid={testid} aria-label={etiqueta} title={etiqueta}
      onClick={apagado ? alTocarApagado : onClick}
      aria-disabled={apagado || undefined}
      style={apagado ? { opacity: OPACIDAD_SIN_RED } : undefined}
      className={`relative shrink-0 w-11 h-11 md:w-10 md:h-10 rounded-xl flex items-center justify-center text-[#9A9A9A] hoverable:text-[#F87171] hoverable:bg-[#2A1414] focus:outline-none focus-visible:ring-2 focus-visible:ring-white ${APRETADO}`}>
      <Trash2 className="w-4 h-4" aria-hidden="true" />
    </button>
  );
}

function EsqueletoDePendientes() {
  return (
    <div className="flex flex-col gap-4" data-testid="pending-loading" aria-hidden="true">
      {[0, 1].map((i) => (
        <div key={i} className={`${TARJETA_PENDIENTE} border-[#222222]`}>
          <div className="flex items-start gap-3">
            <BloqueEsqueleto className="w-10 h-10 rounded-xl shrink-0" />
            <div className="flex-1 flex flex-col gap-2">
              <BloqueEsqueleto className="h-4 w-3/4" />
              <BloqueEsqueleto className="h-3.5 w-1/2" />
            </div>
          </div>
          <BloqueEsqueleto className="h-[22px] w-full rounded-[4px]" />
          <BloqueEsqueleto className="h-4 w-1/3" />
          <BloqueEsqueleto className="h-12 w-full rounded-xl" />
        </div>
      ))}
    </div>
  );
}

export default function PendingPage() {
  const { t, ready, i18n } = useReadyTranslation(['dashboard', 'common']);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { groups, loading, refresh, totalPending, maxPending, isOverTope, snapshot, offline, loadFailed, hasSummary } = usePendingSummary({ pollWhileProcessing: true });
  const [resumeGroup, setResumeGroup] = useState(null);
  const [resumeLoading, setResumeLoading] = useState(false);
  const [showPack, setShowPack] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  // pantallas-alumno (G): el borrado del grupo en vuelo (su boton trabaja con giro)
  const [borrandoGrupo, setBorrandoGrupo] = useState(false);
  // post-t7-1 (H4): el documento entero que se esta mandando a procesar (arriba con los demas: antes de cualquier return)
  const [procesandoEntero, setProcesandoEntero] = useState(null);
  const [retryingPart, setRetryingPart] = useState(null);   // id de la parte en reintento
  const [confirmDeletePart, setConfirmDeletePart] = useState(null);  // {g, p} de la rota a borrar
  const [deletingPart, setDeletingPart] = useState(null);   // id de la parte en borrado
  // Intent pattern (no z-index stacking): opening the pack modal from the resume modal
  // HIDES the resume modal and snapshots the group; closing it RESTORES the resume modal
  // synchronously in the same handler (restore-first), then refreshes in parallel and
  // hot-updates the open modal when fresh numbers land.
  const [packIntent, setPackIntent] = useState(null);  // snapshot of the group being resumed

  const openPackFromResume = () => {
    setPackIntent(resumeGroup);   // keep the data the modal was showing
    setResumeGroup(null);
    setShowPack(true);            // batched → one commit: resume out, pack in, curtain up
  };

  const closePack = () => {
    setShowPack(false);
    if (packIntent) {
      // Restore-FIRST, in the SAME render cycle as the pack unmount (zero frames without
      // an overlay), showing the data the modal already had.
      setResumeGroup(packIntent);
      setPackIntent(null);
    }
    refresh();                    // parallel; the hot-update effect below applies the result
  };

  // Hot-update the OPEN resume modal whenever the summary refreshes: if the user bought
  // credits, the fresh affordability appears by itself. Edge: balances dropped while the
  // modal was open -> no longer processable -> close it (the inline card shows the reason).
  useEffect(() => {
    setResumeGroup(prev => {
      if (!prev) return prev;
      const fresh = groups.find(g => g.group === prev.group);
      if (!fresh) return prev;
      if (!fresh.processable) return null;
      return fresh;
    });
  }, [groups]);
  // Deep link (?group=parent_doc_id from the library strip / duplicate notice): once the
  // list loads, scroll the target group into view and mark it briefly.
  // O3: timers live in refs, cleared ONLY on unmount (the ring turns on at arrival and off
  // ~2.5s later, period). pantallas-alumno (G), PE23: la marca es la de la parte 2 (borde
  // naranja y halo de 3 px) y es la MISMA en la web y en la app.
  const [highlightGroup, setHighlightGroup] = useState(null);
  const deepLinkDone = useRef(false);
  const highlightTimersRef = useRef([]);
  useEffect(() => {
    if (deepLinkDone.current || loading) return;
    const target = searchParams.get('group');
    if (!target || !groups.some(g => g.group === target || g.parent_doc_id === target)) return;
    deepLinkDone.current = true;
    const match = groups.find(g => g.group === target || g.parent_doc_id === target);
    setHighlightGroup(match.group);
    // Let the cards paint first, then scroll.
    highlightTimersRef.current.push(setTimeout(() => {
      const el = document.querySelector(`[data-testid="pending-group-${match.group}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100));
    highlightTimersRef.current.push(setTimeout(() => setHighlightGroup(null), 2500));
  }, [loading, groups, searchParams]);
  useEffect(() => () => { highlightTimersRef.current.forEach(clearTimeout); }, []);

  // i18n cargando → silueta de la lista, no spinner (F4.3b).
  if (!ready) {
    return (
      <div className="max-w-[720px] mx-auto" data-testid="pending-page">
        <EsqueletoDePendientes />
      </div>
    );
  }

  // PEND-C2 — UNA MUTACIÓN QUE NO SALIÓ POR FALTA DE RED NO BORRA LA COPIA.
  // `notifyPendingChanged()` invalida la llave 'pending-summary' del cache de pantalla; sin
  // señal el servidor no cambió nada, así que anunciar un cambio era destruir la última
  // foto a cambio de nada. La frontera es la del repo (`isOfflineShapedError`).
  const afterMutation = async (err) => {
    await refresh();
    if (err && isOfflineShapedError(err)) return;
    notifyPendingChanged();
  };

  // Modal (resume mode) primary → POST /documents/process-pending {parent_doc_id|doc_id, count}
  const handleResumeProcess = async (count) => {
    pedirPermisoSiToca();
    if (!resumeGroup) return;
    setResumeLoading(true);
    let fallo = null;   // PEND-C2: el fallo viaja hasta `afterMutation`
    try {
      const body = { count };
      if (resumeGroup.parent_doc_id) body.parent_doc_id = resumeGroup.parent_doc_id;
      else body.doc_id = resumeGroup.group;
      const res = await api.post('/documents/process-pending', body);
      const processed = res.data?.processed ?? 0;
      if (processed > 0) {
        toast.success(t('dashboard:pending.processing_toast', { count: processed, n: processed }));
        document.dispatchEvent(new CustomEvent('studybuddy:doc-uploaded'));
      } else {
        // ── TANDA L / L4.1 - EL CERO QUE YA NO ES AVERÍA: el servidor dice POR QUÉ no procesó
        // nada cuando el motivo es el saldo (`reason`). Un backend viejo no lo manda y esto cae
        // al genérico de siempre.
        const razon = res.data?.reason;
        const necesita = res.data?.credits_required;
        const tiene = res.data?.credits_available;
        if (razon === 'insufficient_slots') {
          toast.error(t('dashboard:pending.process_no_slots_toast'));
        } else if ((razon === 'insufficient_credits' || razon === 'insufficient_both')
                   && typeof necesita === 'number' && typeof tiene === 'number') {
          toast.error(t('dashboard:pending.next_part_line', { count: necesita, have: tiene }));
        } else {
          toast.error(t('dashboard:pending.process_failed_toast'));
        }
      }
    } catch (err) {
      fallo = err;
      const s = err.response?.status;
      toast.error(s === 404 || s === 410 ? t('dashboard:pending.expired_toast') : t('dashboard:pending.process_failed_toast'));
    } finally {
      setResumeGroup(null);
      setResumeLoading(false);
      await afterMutation(fallo);
    }
  };

  // post-t7-1 (H4): procesar un documento entero desde su renglon: un solo cobro (process-pending con `doc_id`), y
  // el resultado es un documento normal en el Panel.
  const procesarEntero = async (g) => {
    pedirPermisoSiToca();
    setProcesandoEntero(g.group);
    let fallo = null;
    try {
      const res = await api.post('/documents/process-pending', { doc_id: g.group, count: 1 });
      if ((res.data?.processed ?? 0) > 0) {
        toast.success(t('dashboard:pending.entero.procesando_toast'));
        document.dispatchEvent(new CustomEvent('studybuddy:doc-uploaded'));
      } else if (res.data?.reason === 'insufficient_slots') {
        toast.error(t('dashboard:pending.process_no_slots_toast'));
      } else if (typeof res.data?.credits_required === 'number' && typeof res.data?.credits_available === 'number') {
        toast.error(t('dashboard:auto_split.line_full', { count: res.data.credits_required, have: res.data.credits_available }));
      } else {
        toast.error(t('dashboard:pending.process_failed_toast'));
      }
    } catch (err) {
      fallo = err;
      const s = err.response?.status;
      toast.error(s === 404 || s === 410 ? t('dashboard:pending.expired_toast') : t('dashboard:pending.process_failed_toast'));
    } finally {
      setProcesandoEntero(null);
      await afterMutation(fallo);
    }
  };

  const handleDeleteGroup = async (g) => {
    let fallo = null;   // PEND-C2
    setBorrandoGrupo(true);
    try {
      if (g.documento_entero) {
        // post-t7-1 (H4): un documento entero se borra como documento (no es un grupo de partes)
        await api.delete(`/documents/${g.group}`);
        toast.success(t('dashboard:toasts.delete_success'));
        setConfirmDelete(null);
        setBorrandoGrupo(false);
        await afterMutation(null);
        return;
      }
      await api.delete(`/documents/group/${g.parent_doc_id || g.group}`);
      toast.success(t('dashboard:pending.deleted_toast'));
    } catch (err) {
      fallo = err;
      toast.error(t('dashboard:toasts.delete_failed'));
    }
    setConfirmDelete(null);
    setBorrandoGrupo(false);
    await afterMutation(fallo);
  };

  // P-F2 - REINTENTAR UNA PARTE QUE FALLO, SIN VOLVER A SUBIR EL ARCHIVO. La cadena es la
  // MISMA que la tarjeta de error del Dashboard: /retry deja el doc en 'uploaded' sin cobrar,
  // /process cobra. Si el pipeline falla, el reembolso corre solo.
  const handleRetryPart = async (docId) => {
    setRetryingPart(docId);
    let fallo = null;   // PEND-C2
    try {
      const r1 = await api.post(`/documents/${docId}/retry`);
      if (r1.data?.status === 'uploaded') {
        await api.post(`/documents/${docId}/process`);
      }
      toast.success(t('dashboard:pending.retry_toast'));
      document.dispatchEvent(new CustomEvent('studybuddy:doc-uploaded'));
    } catch (err) {
      fallo = err;
      // R-17 (remate F): la clasificación completa vive en lib/retryErrorToast.js, la MISMA
      // que usa el Panel. `serverDetailFallback` queda APAGADO aquí a propósito.
      toast.error(retryErrorToast(err, t, {
        insufficient: 'dashboard:pending.retry_no_credits',
        insufficientN: 'dashboard:pending.retry_no_credits_n',
      }));
    } finally {
      setRetryingPart(null);
      await afterMutation(fallo);
    }
  };

  // ── TANDA L / L2.2 - BORRAR **ESA** PARTE, CON UN TOQUE: el borrado individual que ya
  // existe (`DELETE /documents/{id}`), cascada por ese id y NADA más.
  const handleDeletePart = async (docId) => {
    setDeletingPart(docId);
    let fallo = null;   // PEND-C2
    try {
      await api.delete(`/documents/${docId}`);
      toast.success(t('dashboard:pending.part_deleted_toast'));
    } catch (err) {
      fallo = err;
      toast.error(t('dashboard:toasts.delete_failed'));
    }
    setConfirmDeletePart(null);
    setDeletingPart(null);
    await afterMutation(fallo);
  };

  const onCardSelect = (c) => {
    // series-7 (#489): una oferta de `series7` (lleva `kind`) abre la compra que ya
    // existe: paquetes -> el selector de paquetes; Pro o Pro mas paquetes -> /upgrade.
    if (c.kind) {
      const accion = accionDeOferta(c);
      if (accion === 'packs') { trackEvent('cta_click', { cta: 'buy_pack', wall: 'pending' }); setShowPack(true); }
      else if (accion === 'pro') { trackEvent('cta_click', { cta: 'upgrade_pro', wall: 'pending' }); navigate('/upgrade'); }
      return;
    }
    if (c.instrument === 'pack') { trackEvent('cta_click', { cta: 'buy_pack', wall: 'pending' }); setShowPack(true); }
    else { trackEvent('cta_click', { cta: `upgrade_${c.instrument}`, wall: 'pending' }); navigate('/upgrade'); }
  };

  // P6 (firma del 19-oct): sin red con la lista ya cargada, lo que manda algo se ve apagado y avisa.
  const sinRed = Boolean(offline) && groups.length > 0;
  const avisoSinRed = () => avisarSinRed(t);

  const chipDeCaducidad = (g) => {
    const d = daysUntil(g.nearest_expires_at);
    if (d === null || d > EXPIRY_NOTICE_DAYS) return null;
    return (
      <ChipDeEstado tono={d <= EXPIRY_URGENT_DAYS ? 'rojo' : 'gris'} icono="reloj" testid={`pending-expiry-${g.group}`}>
        {t('dashboard:pending.expires_in', { count: d, days: d })}
      </ChipDeEstado>
    );
  };

  const ofertas = (g) => (
    <AutoSplitSuggestionCards
      t={t}
      suggestions={g.affordability?.suggestions}
      n={g.affordability?.n}
      onSelect={onCardSelect}
      testidPrefix={`pending-${g.group}`}
      series7={g.affordability?.series7}
      idioma={i18n?.language}
      estilo="alumno"
      apagado={sinRed}
      alTocarApagado={avisoSinRed}
    />
  );

  const encabezadoDeTarjeta = (g, { icono: Icono, nombreTestid, bote }) => (
    <div className="flex items-start gap-3 min-w-0">
      <div className={CAJA_ICONO} aria-hidden="true"><Icono className={TRAZO_ICONO} /></div>
      <p className="flex-1 min-w-0 pt-2 text-[16px] leading-[22px] font-semibold text-[#F4F4F3] line-clamp-2 break-words"
        title={g.original_filename} data-testid={nombreTestid}>
        {g.original_filename}
      </p>
      {bote}
    </div>
  );

  const claseDeTarjeta = (g) => `${TARJETA_PENDIENTE} ${highlightGroup === g.group ? MARCA_DE_LLEGADA : 'border-[#222222]'}`;

  return (
    <div className="max-w-[720px] mx-auto flex flex-col gap-4 pb-[max(1.5rem,env(safe-area-inset-bottom))]" data-testid="pending-page">
      <EncabezadoDePagina titulo={t('dashboard:pending.page_title')} texto={t('dashboard:pending.page_subtitle')} />

      {/* You have: credits, document spaces, Pending storage - the same three numbers as the modal */}
      {snapshot && (
        <HaveChips t={t} credits={snapshot.credits_available} slots={snapshot.slots_free} pending={snapshot.pending_free}
          testid="pending-have-chips" estilo="alumno" />
      )}

      {/* Downgrade / grandfather banner (3.5): el aviso naranja suave del Panel. */}
      {isOverTope && (
        <AvisoDelPanel tono="naranja" icono={AlertTriangle} testid="pending-downgrade-banner"
          titulo={t('dashboard:pending.downgrade_banner', { count: totalPending, allow: maxPending })} />
      )}

      {/* F6.8 EH-4(a) - SIN SEÑAL, LA NOTA HONESTA, Y ENCIMA DE LO QUE YA SE SABE. Si no se sabe
          nada, esto es TODO lo que se pinta: nunca "No tienes pendientes". El aviso gris del Panel. */}
      {offline && (
        <AvisoDelPanel tono="gris" icono={CloudOff} testid="pending-offline-notice" titulo={t('dashboard:pending.offline_notice')} />
      )}

      {/* F6.8 R-2 - CON SEÑAL Y EL SERVIDOR CAÍDO, LA PÁGINA YA NO SE QUEDA MUDA. Con una lista ya
          conocida, el renglon gris con el mensaje y "Retry" (PE10-b); sin nada, el recuadro I2
          (PE10-a). El botón dispara el `refresh()` del hook, que es LA MISMA carga. */}
      {loadFailed && !offline && (groups.length > 0 ? (
        <AvisoDelPanel tono="gris" icono={CloudOff} testid="pending-load-error" titulo={t('dashboard:pending.load_error.body')}
          acciones={(
            <button type="button" onClick={() => refresh()} className={`${BOTON_AVISO_PRINCIPAL} ${APRETADO}`} data-testid="pending-retry-btn">
              {t('dashboard:pending.load_error.cta')}
            </button>
          )} />
      ) : (
        <div data-testid="pending-load-error">
          <RecuadroDeEstado tipo="no_cargo" texto={t('dashboard:pending.load_error.body')}
            accion={{ texto: t('dashboard:pending.load_error.cta'), onClick: () => refresh(), icono: RotateCw, testid: 'pending-retry-btn' }} />
        </div>
      ))}

      {loading && groups.length === 0 && <EsqueletoDePendientes />}

      {/* Empty state — RESERVADO a la respuesta exitosa vacía (`hasSummary`). */}
      {!loading && hasSummary && groups.length === 0 && (
        <RecuadroDeEstado tipo="vacio" icono={FileClock} testid="pending-empty" texto={t('dashboard:pending.empty_state')} />
      )}

      <div className="flex flex-col gap-4">
        {groups.map((g) => {
          const total = g.total_count ?? (g.ready_count + g.pending_count);
          const rotas = (g.parts || []).filter(p => p.status === 'error' && p.id);
          // El saldo con el que se mide CADA botón de esta tarjeta: el mismo número que ya
          // usa el renglón del botón grande, para que los dos cuenten la misma historia.
          const saldo = g.affordability?.snapshot?.credits_available;
          // L3.2 — el grupo que sigue listado SÓLO por su parte rota: sin "Sigue:", sin
          // botón grande y sin bote de grupo.
          const soloRota = (g.pending_count || 0) === 0;
          const llegada = highlightGroup === g.group ? 'si' : undefined;
          // post-t7-1 (H4): un escaneado guardado ENTERO (no tiene partes): su tarjeta propia, sin mapa ni liga.
          if (g.documento_entero) {
            const ejes = g.affordability?.binding ?? 'credits';
            return (
              <div key={g.group} className={claseDeTarjeta(g)} data-testid={`pending-group-${g.group}`} data-llegada={llegada}>
                {encabezadoDeTarjeta(g, {
                  icono: FileText,
                  nombreTestid: `pending-entero-nombre-${g.group}`,
                  bote: <Bote onClick={() => setConfirmDelete(g)} testid={`pending-delete-${g.group}`}
                    etiqueta={t('dashboard:doc_card.delete_btn_aria')} apagado={sinRed} alTocarApagado={avisoSinRed} />,
                })}
                <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5">
                  <p className="text-[13.5px] leading-5 text-[#9A9A9A]" data-testid={`pending-entero-linea-${g.group}`}>
                    {t('dashboard:auto_split.line_full', { count: g.next_part_cost, have: saldo ?? 0 })}
                  </p>
                  {chipDeCaducidad(g)}
                </div>
                <Boton alto="grande" aLoAncho testid={`pending-resume-${g.group}`}
                  disabled={!g.processable} trabajando={procesandoEntero === g.group}
                  sinRed={sinRed && g.processable} alTocarApagado={avisoSinRed}
                  onClick={() => g.processable && procesarEntero(g)}>
                  {t('dashboard:pending.entero.procesar', { count: g.next_part_cost })}
                </Boton>
                {!g.processable && (ejes === 'slots' || ejes === 'both') && (
                  <div className="flex flex-col gap-2">
                    <p className={RENGLON_FRENO} data-testid={`pending-blocked-slots-${g.group}`}>{t('dashboard:pending.entero.sin_espacio')}</p>
                    <Liga onClick={() => navigate('/dashboard')} testid={`pending-free-space-${g.group}`}>
                      {t('dashboard:pending.free_space_library_btn')}
                    </Liga>
                  </div>
                )}
                {!g.processable && ofertas(g)}
              </div>
            );
          }
          return (
            <div key={g.group} className={claseDeTarjeta(g)} data-testid={`pending-group-${g.group}`} data-llegada={llegada}>
              {encabezadoDeTarjeta(g, {
                icono: Layers,
                nombreTestid: `pending-group-name-${g.group}`,
                bote: !soloRota ? (
                  <Bote onClick={() => setConfirmDelete(g)} testid={`pending-delete-${g.group}`}
                    etiqueta={t('dashboard:pending.delete_group')} apagado={sinRed} alTocarApagado={avisoSinRed} />
                ) : null,
              })}

              <div className="flex flex-col gap-3">
                <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5">
                  <p className="text-[13.5px] leading-5 text-[#9A9A9A]" data-testid={`pending-counts-${g.group}`}>
                    {(() => {
                      // Always sums to total: ready(+partial) + processing + pending + failed (L3.4).
                      const processing = g.processing_count || 0;
                      const pendingParts = g.pending_count || 0;
                      const segs = [t('dashboard:pending.seg_ready', { ready: g.ready_count, total })];
                      if (processing > 0) segs.push(t('dashboard:pending.seg_processing', { count: processing }));
                      if (pendingParts > 0) segs.push(t('dashboard:pending.seg_pending', { count: pendingParts }));
                      if (rotas.length > 0) segs.push(t('dashboard:pending.seg_failed', { count: rotas.length }));
                      return segs.join(', ');
                    })()}
                  </p>
                  {chipDeCaducidad(g)}
                </div>

                <PartsMap segments={buildCardSegments(g.parts, g.next_chunk_index)} t={t} testid={`parts-map-${g.group}`} estilo="alumno" />
                {g.pending_count > 0 && g.next_chunk_index != null && (
                  <p className="text-[14px] leading-5 font-semibold text-[#C8C8C8]" data-testid={`pending-next-action-${g.group}`}>
                    {t('dashboard:pending.next_action', { i: g.next_chunk_index, count: g.next_part_cost })}
                  </p>
                )}
                {g.ready_count > 0 && (
                  <Liga onClick={() => navigate('/dashboard')} testid={`pending-view-library-${g.group}`}>
                    {t('dashboard:pending.view_ready_in_library')}
                  </Liga>
                )}
              </div>

              {/* P-F2 y TANDA L / L2 - LA PARTE QUE FALLO, CON SALIDA, Y EL BOTON QUE NO PROMETE LO QUE NO
                  PUEDE (los tres estados se deciden ANTES del toque con `retry_cost` y `retry_attempts_left`).
                  pantallas-alumno (G): el recuadro rojo suave de la parte 2, con el boton SECUNDARIO dentro. */}
              {rotas.map((p) => {
                const estado = estadoDeParteRota(p, saldo);
                return (
                  <div key={p.id} className="rounded-[14px] border border-[#3D1C1C] bg-[#1C1010] p-3 flex flex-col gap-2.5"
                    data-testid={`pending-part-error-${g.group}-${p.chunk_index}`}>
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2.5">
                      <p className="min-w-0 flex items-center gap-1.5 text-[14px] leading-5 font-semibold text-[#F87171]">
                        <AlertTriangle className="w-4 h-4 shrink-0" aria-hidden="true" />
                        <span className="min-w-0">{t('dashboard:pending.part_error')}</span>
                      </p>
                      {estado === 'agotado' ? (
                        /* L2.2 - sin intentos, la salida es borrar ESA parte (con confirmación). */
                        <Boton variante="secundarioRojo" icono={Trash2} className="max-md:w-full shrink-0"
                          testid={`pending-delete-part-${p.id}`} trabajando={deletingPart === p.id}
                          sinRed={sinRed} alTocarApagado={avisoSinRed}
                          onClick={() => setConfirmDeletePart({ g, p })}>
                          {t('dashboard:pending.delete_part_btn')}
                        </Boton>
                      ) : (
                        <Boton variante="secundario" icono={RotateCw} className="max-md:w-full shrink-0"
                          testid={`pending-retry-part-${p.id}`} trabajando={retryingPart === p.id}
                          disabled={estado === 'sin_saldo'}
                          sinRed={sinRed && estado !== 'sin_saldo'} alTocarApagado={avisoSinRed}
                          onClick={() => handleRetryPart(p.id)}>
                          {/* Con precio cuando el servidor lo manda; sin él (PE24), la etiqueta de siempre. */}
                          {tieneNumero(p.retry_cost)
                            ? t('dashboard:pending.retry_with_cost', { count: p.retry_cost })
                            : t('dashboard:doc_card.retry_btn')}
                        </Boton>
                      )}
                    </div>
                    {/* L2.1 - el porqué del apagado, con los mismos dos números y la MISMA salida de compra. */}
                    {estado === 'sin_saldo' && (
                      <div className="flex flex-col gap-2" data-testid={`pending-part-blocked-${p.id}`}>
                        <p className="text-[13.5px] leading-5 text-[#D9C4C4]">
                          {t('dashboard:pending.retry_cost_line', { count: p.retry_cost, have: saldo ?? 0 })}
                        </p>
                        <Liga testid={`pending-part-buy-${p.id}`} apagada={sinRed} alTocarApagada={avisoSinRed}
                          onClick={() => { trackEvent('cta_click', { cta: 'buy_pack', wall: 'pending_retry' }); setShowPack(true); }}>
                          {t('dashboard:insufficient_credits.buy_pack_btn')}
                        </Liga>
                      </div>
                    )}
                    {estado === 'agotado' && tieneNumero(p.retry_limit) && (
                      <p className="text-[13.5px] leading-5 text-[#D9C4C4]" data-testid={`pending-part-exhausted-${p.id}`}>
                        {t('dashboard:pending.retry_exhausted', { count: p.retry_limit })}
                      </p>
                    )}
                  </div>
                );
              })}

              {/* CTA — process button ALWAYS visible (enabled when processable, else disabled).
                  L3.2: el grupo que sólo sigue aquí por su parte rota no tiene "siguiente parte". */}
              {!soloRota && (
                <div className="flex flex-col gap-3">
                  <Boton alto="grande" aLoAncho testid={`pending-resume-${g.group}`}
                    disabled={!g.processable}
                    sinRed={sinRed && g.processable} alTocarApagado={avisoSinRed}
                    onClick={() => g.processable && setResumeGroup(g)}>
                    {t('dashboard:pending.process_next', { count: g.next_part_cost })}
                  </Boton>
                  {!g.processable && (
                    <div className="flex flex-col gap-2" data-testid={`pending-blocked-${g.group}`}>
                      {/* TANDA L / L3.1 - EL MOTIVO REAL, NO SIEMPRE "CRÉDITOS" (`binding`); con 'both' los dos
                          renglones; sin `binding` (backend viejo) el renglón de créditos de siempre. */}
                      {(g.affordability?.binding ?? 'credits') !== 'slots' && (
                        <p className={RENGLON_FRENO} data-testid={`pending-blocked-credits-${g.group}`}>
                          {t('dashboard:pending.next_part_line', { count: g.next_part_cost, have: saldo ?? 0 })}
                        </p>
                      )}
                      {(g.affordability?.binding === 'slots' || g.affordability?.binding === 'both') && (
                        <>
                          <p className={RENGLON_FRENO} data-testid={`pending-blocked-slots-${g.group}`}>
                            {t('dashboard:pending.next_part_space_line')}
                          </p>
                          {/* La salida que corresponde a ESE motivo: liberar un espacio en la biblioteca. */}
                          <Liga onClick={() => navigate('/dashboard')} testid={`pending-free-space-${g.group}`}>
                            {t('dashboard:pending.free_space_library_btn')}
                          </Liga>
                        </>
                      )}
                      {ofertas(g)}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Resume modal (same AutoSplitModal, resume mode) */}
      {resumeGroup && (
        <AutoSplitModal
          isOpen
          mode="resume"
          preview={{
            affordability: resumeGroup.affordability,
            original_filename: resumeGroup.original_filename,
            total_chunks: resumeGroup.affordability?.n,
            next_part_cost: resumeGroup.next_part_cost,
            parts: resumeGroup.parts,
            next_chunk_index: resumeGroup.next_chunk_index,
          }}
          activeDocs={[]}
          onProcess={handleResumeProcess}
          onClose={() => setResumeGroup(null)}
          onBuyPack={openPackFromResume}
          onUpgrade={() => navigate('/upgrade')}
          confirmLoading={resumeLoading}
        />
      )}

      {/* R3: curtain continuity — while the resume->pack->resume cycle is in flight, keep a
          backdrop mounted UNDER both modals so there is never a frame without an overlay. */}
      {packIntent && (
        <div className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm" data-testid="modal-transition-backdrop" />
      )}

      <CreditPackModal open={showPack} onClose={closePack} fromAutoSplit={false} />

      {/* Delete confirmation (PE6-a, b, d): la ventana del alumno con su X, "Cancel" y el rojo de borrar.
          TANDA L / L3.3 (censo #18) - EL BOTE DICE LA VERDAD ENTERA: la variante que avisa que la parte
          fallida se queda sale SOLO cuando de verdad hay una parte rota. */}
      {confirmDelete && (
        <Ventana
          testid="pending-delete-confirm"
          etiquetaId="pending-delete-confirm-title"
          etiquetaCerrar={t('common:cancel', 'Cancel')}
          alCerrar={() => { if (!borrandoGrupo) setConfirmDelete(null); }}
          titulo={confirmDelete.documento_entero
            ? t('dashboard:partes_padre.borrar_titulo', { name: confirmDelete.original_filename })
            : t('dashboard:pending.delete_confirm_title')}
          botones={(
            <>
              <button type="button" onClick={() => setConfirmDelete(null)} disabled={borrandoGrupo}
                className={`${BOTON_VENTANA_SECUNDARIO} ${APRETADO}`} data-testid="pending-delete-cancel">
                {t('dashboard:pending.delete_cancel')}
              </button>
              <button type="button" onClick={() => handleDeleteGroup(confirmDelete)} disabled={borrandoGrupo}
                data-trabajando={borrandoGrupo ? 'true' : undefined}
                className={`relative ${BOTON_VENTANA_BORRAR} ${APRETADO}`} data-testid="pending-delete-confirm-btn">
                <GiroEncima trabajando={borrandoGrupo}>{t('dashboard:pending.delete_confirm')}</GiroEncima>
              </button>
            </>
          )}
        >
          <p className="text-[14px] leading-5 text-[#C8C8C8]" data-testid="pending-delete-confirm-body">
            {confirmDelete.documento_entero ? t('dashboard:pending.entero.borrar_cuerpo')
              : t((confirmDelete.parts || []).some(p => p.status === 'error')
                ? 'dashboard:pending.delete_confirm_body_failed'
                : 'dashboard:pending.delete_confirm_body',
               { name: confirmDelete.original_filename })}
          </p>
        </Ventana>
      )}

      {/* L2.2 - Confirmación del borrado de UNA parte rota (PE6-c): misma ventana; el rojo trabaja con
          giro y "Cancel" se apaga mientras tanto (PE22-b). */}
      {confirmDeletePart && (
        <Ventana
          testid="pending-delete-part-confirm"
          etiquetaId="pending-delete-part-confirm-title"
          etiquetaCerrar={t('common:cancel', 'Cancel')}
          alCerrar={() => { if (!deletingPart) setConfirmDeletePart(null); }}
          titulo={t('dashboard:pending.delete_part_confirm_title', { i: confirmDeletePart.p.chunk_index })}
          botones={(
            <>
              <button type="button" onClick={() => setConfirmDeletePart(null)} disabled={deletingPart === confirmDeletePart.p.id}
                className={`${BOTON_VENTANA_SECUNDARIO} ${APRETADO}`} data-testid="pending-delete-part-cancel">
                {t('dashboard:pending.delete_cancel')}
              </button>
              <button type="button"
                onClick={() => handleDeletePart(confirmDeletePart.p.id)}
                disabled={deletingPart === confirmDeletePart.p.id}
                data-trabajando={deletingPart === confirmDeletePart.p.id ? 'true' : undefined}
                className={`relative ${BOTON_VENTANA_BORRAR} ${APRETADO}`}
                data-testid="pending-delete-part-confirm-btn"
              >
                <GiroEncima trabajando={deletingPart === confirmDeletePart.p.id}>{t('dashboard:pending.delete_part_confirm')}</GiroEncima>
              </button>
            </>
          )}
        >
          <p className="text-[14px] leading-5 text-[#C8C8C8]">
            {t('dashboard:pending.delete_part_confirm_body', {
              i: confirmDeletePart.p.chunk_index,
              name: confirmDeletePart.g.original_filename,
            })}
          </p>
        </Ventana>
      )}
    </div>
  );
}
